import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';

import {
  addLanguage,
  deleteLanguage,
  updateDefaultLanguage,
} from '../../languages/actions';
import { deleteTranslation } from '../../translations/actions';
import { updateDisclaimerMessage } from '../actions';
import { disclaimerMessageReferencesAdapter } from '../adapters';
import {
  DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
  DEFAULT_DISCLAIMER_MESSAGE,
} from '../constants';
import type { DisclaimerMessage } from '../types';

export const disclaimerMessageAdapter = createEntityAdapter<
  DisclaimerMessage,
  string
>({
  selectId: disclaimerMessage =>
    disclaimerMessage.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
});

const initialState = disclaimerMessageAdapter.getInitialState();

const slice = createSlice({
  name: 'disclaimerMessage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateDisclaimerMessage, (state, action) => {
      const existingDisclaimerMessage =
        state.entities[action.payload.languageCode];

      if (existingDisclaimerMessage) {
        const { languageCode: _ignored, ...patch } = action.payload;
        assignDefinedFields(existingDisclaimerMessage, patch);
        assignMetadata(action, existingDisclaimerMessage);
        disclaimerMessageAdapter.updateOne(state, {
          id: action.payload.languageCode,
          changes: existingDisclaimerMessage,
        });
      } else {
        const newDisclaimerMessage: DisclaimerMessage = {
          ...DEFAULT_DISCLAIMER_MESSAGE,
          languageCode: action.payload.languageCode,
        };
        assignDefinedFields(newDisclaimerMessage, action.payload);
        assignMetadata(action, newDisclaimerMessage);
        disclaimerMessageAdapter.addOne(state, newDisclaimerMessage);
      }
    });

    builder.addCase(addLanguage, (state, action) => {
      if (!state.entities[action.payload.code]) {
        const newDisclaimerMessage: DisclaimerMessage = {
          ...DEFAULT_DISCLAIMER_MESSAGE,
        };
        assignDefinedFields(newDisclaimerMessage, {
          languageCode: action.payload.code,
        });
        assignMetadata(action, { ...newDisclaimerMessage });

        disclaimerMessageAdapter.addOne(state, newDisclaimerMessage);
      }
    });

    builder.addCase(updateDefaultLanguage, (state, action) => {
      if (!state.entities[action.payload.languageCode]) {
        const newDisclaimerMessage: DisclaimerMessage = {
          ...DEFAULT_DISCLAIMER_MESSAGE,
        };

        assignDefinedFields(newDisclaimerMessage, {
          languageCode: action.payload.languageCode,
        });
        assignMetadata(action, { ...newDisclaimerMessage });

        disclaimerMessageAdapter.addOne(state, newDisclaimerMessage);
      }
    });

    // update disclaimer message when language is removed
    builder.addCase(deleteLanguage, (state, action) => {
      const { code: languageCode } = action.payload;
      if (state.entities[languageCode] !== undefined) {
        disclaimerMessageAdapter.removeOne(state, languageCode);
      }
    });

    builder.addCase(deleteTranslation, (state, action) => {
      const { id: translationId } = action.payload;
      for (const disclaimer of Object.values(state.entities)) {
        if (disclaimer?.references?.translations?.[translationId]) {
          disclaimerMessageReferencesAdapter.removeReference(
            state,
            disclaimer.languageCode ?? DEFAULT_AGENT_SETTINGS_LANGUAGE_CODE,
            'translations',
            translationId,
          );
        }
      }
    });
  },
});

export default slice.reducer;
